import { useState, type FormEvent } from 'react';
import { formatDate } from '@brimo/shared';
import { Banner, Button, Skeleton, TextField, useToast } from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { useOrganizationBilling, useSaveInvoiceEmail, type OrganizationBilling } from '../../billing/api';
import { ActionReason, InvoiceList, PlanCard } from '../../billing/ui';
import billingStyles from '../../billing/BillingPage.module.css';
import { OrganizationPage, QueryError } from '../users/OrganizationPage';
import styles from './OrganizationBillingPage.module.css';

/**
 * „Abrechnung der Organisation" (Einstellungen 28): the banner of an open invoice, the tariff with its grid, the
 * invoices, the payment method and „Rechnungsempfang" (the only action that works without a payment provider:
 * `PUT …/invoice-email`). Every other action is disabled with the server's reason (D-07).
 */
export function OrganizationBillingPage() {
  const billing = useOrganizationBilling();
  const data = billing.data;
  return (
    <OrganizationPage title="Abrechnung der Organisation">
      {billing.isError ? <QueryError error={billing.error} /> : null}
      {billing.isPending ? (
        <div className={billingStyles.grid} aria-busy="true">
          <div className={billingStyles.card}>
            <Skeleton width="30%" height={18} />
            <Skeleton variant="block" height={72} />
            <Skeleton variant="block" height={64} />
          </div>
          <div className={billingStyles.sideCard}>
            <Skeleton width="50%" height={16} />
            <Skeleton variant="block" height={50} />
          </div>
        </div>
      ) : null}
      {data ? <OrganizationBillingView billing={data} /> : null}
    </OrganizationPage>
  );
}

function OrganizationBillingView({ billing }: { billing: OrganizationBilling }) {
  const { payNow, changePaymentMethod, adjustSeats, changePlan } = billing.actions;
  return (
    <>
      {billing.banner ? (
        <section className={styles.banner} aria-label={billing.banner.title}>
          <div className={styles.bannerText}>
            <span className={styles.bannerTitle}>{billing.banner.title}</span>
            <span className={styles.bannerBody}>{billing.banner.text}</span>
            <ActionReason actions={[payNow]} />
          </div>
          <Button size="small" className={styles.bannerButton} disabled={!payNow.allowed}>
            Jetzt bezahlen
          </Button>
        </section>
      ) : null}
      <div className={billingStyles.grid}>
        <div className={billingStyles.column}>
          <section className={billingStyles.card} aria-label="Tarif">
            <div className={billingStyles.cardHead}>
              <h3 className={billingStyles.cardTitle}>Tarif</h3>
              <span className={billingStyles.cardMeta}>{billing.plan.cycle}</span>
            </div>
            <PlanCard
              name={billing.plan.name}
              detail={billing.plan.usage.label}
              priceLabel={billing.plan.priceLabel}
            />
            <dl className={styles.grid}>
              {billing.plan.grid.map((cell) => (
                <div key={cell.label} className={styles.gridCell}>
                  <dt className={styles.gridLabel}>{cell.label}</dt>
                  <dd className={styles.gridValue}>{cell.value}</dd>
                </div>
              ))}
            </dl>
            <div className={billingStyles.buttons}>
              <Button variant="secondary" size="small" disabled={!adjustSeats.allowed}>
                Plätze anpassen
              </Button>
              <Button variant="secondary" size="small" disabled={!changePlan.allowed}>
                Tarif wechseln
              </Button>
            </div>
            <ActionReason actions={[adjustSeats, changePlan]} />
          </section>
          <InvoiceList invoices={billing.invoices} />
        </div>
        <div className={billingStyles.column}>
          <section className={billingStyles.sideCard} aria-label="Zahlungsmittel">
            <h3 className={billingStyles.sideTitle}>Zahlungsmittel</h3>
            <div className={billingStyles.method}>
              <span className={billingStyles.methodIcon} aria-hidden="true" />
              <div className={billingStyles.methodText}>
                <span className={billingStyles.methodTitle}>Noch kein Zahlungsmittel hinterlegt</span>
                <span className={billingStyles.methodSub}>
                  {billing.plan.trialEndsOn
                    ? `Testphase bis ${formatDate(billing.plan.trialEndsOn)} · danach nötig`
                    : 'Für den aktuellen Tarif nicht nötig.'}
                </span>
              </div>
            </div>
            <Button variant="secondary" size="small" disabled={!changePaymentMethod.allowed}>
              Bankverbindung ändern
            </Button>
            <ActionReason actions={[changePaymentMethod]} />
          </section>
          <InvoiceEmailCard billing={billing} />
        </div>
      </div>
    </>
  );
}

function InvoiceEmailCard({ billing }: { billing: OrganizationBilling }) {
  const { toast } = useToast();
  const save = useSaveInvoiceEmail();
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const change = billing.actions.changeInvoiceEmail;
  const fieldError = save.error instanceof ApiError ? save.error.fields['email'] : undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(email.trim(), {
      onSuccess: () => {
        setEditing(false);
        toast({ message: 'Rechnungsempfang gespeichert.' });
      },
    });
  }

  return (
    <section
      className={`${billingStyles.sideCard} ${billingStyles.sideCardCompact}`}
      aria-label="Rechnungsempfang"
    >
      <h3 className={billingStyles.sideTitle}>Rechnungsempfang</h3>
      {editing ? (
        <form className={billingStyles.addressForm} onSubmit={submit} noValidate>
          <TextField
            label="E-Mail für Rechnungen"
            type="email"
            value={email}
            error={fieldError}
            onChange={(event) => setEmail(event.target.value)}
          />
          {save.isError && !fieldError ? <Banner tone="danger">{errorMessage(save.error)}</Banner> : null}
          <div className={billingStyles.addressActions}>
            <Button type="submit" size="small" loading={save.isPending}>
              Speichern
            </Button>
            <Button type="button" variant="secondary" size="small" onClick={() => setEditing(false)}>
              Abbrechen
            </Button>
          </div>
        </form>
      ) : (
        <>
          <span className={billingStyles.address}>
            {billing.invoiceEmail ?? 'Noch keine Adresse hinterlegt'}
            {'\n'}
            {billing.invoiceEmailNote}
          </span>
          <Button
            variant="secondary"
            size="small"
            className={billingStyles.smallButton}
            disabled={!change.allowed}
            onClick={() => {
              setEmail(billing.invoiceEmail ?? '');
              save.reset();
              setEditing(true);
            }}
          >
            Ändern
          </Button>
          <ActionReason actions={[change]} />
        </>
      )}
    </section>
  );
}
